/**
 * Retrieved-passage list shared by KnowledgeCard (`passages` section) and
 * MetaCard (`rag_context` section).  Each passage shows its text and source;
 * the source links out only when its URL is http(s) (lib/safeUrl).
 */
import type { ReactNode } from "react";
import { isSafeUrl } from "../../lib/safeUrl";

/** One retrieved passage.  Matches backend knowledge-card passages and
 * meta-card rag_context chunks (which carry extra keys, ignored here). */
export interface Passage {
  text: string;
  source?: string;
  url?: string;
}

interface Props {
  passages: Passage[];
  /** Rendered instead of the list when there are no passages. */
  empty: ReactNode;
}

/** An ordered list of passages, or `empty` when there are none. */
export default function PassageList({ passages, empty }: Props) {
  if (passages.length === 0) return <>{empty}</>;
  return (
    <ol className="space-y-3">
      {passages.map((p, i) => (
        <li key={i} className="border-l-2 border-sky-500/30 pl-3 min-w-0">
          <p className="text-sm text-white/80 leading-relaxed break-words">{p.text}</p>
          {p.source && (
            <p className="mt-1 text-[11px] text-white/45 truncate">
              {isSafeUrl(p.url) ? (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-300/80 hover:text-sky-200 transition-colors underline-offset-2 hover:underline"
                >
                  {p.source}
                </a>
              ) : (
                <span>{p.source}</span>
              )}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}
